import React, { useEffect, useState } from "react";
import { useLocation } from 'react-router-dom';
import {
  Typography,
  Button,
  Box,
  TextField,
  Grid2,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { backArrow } from "../../assets/Icons";

import { useDispatch, useSelector } from "react-redux";
import { getPatientList } from "../../redux/reducers/patientReducer";
import CustomGenericTable from "../../components/CustomGenericTable";
import Loader from "../../components/Loader";
import moment from "moment";
import withNavigate from "../../hocs/withNavigate";

// Column definitions
const columns = [
  { id: "patientId", label: "ID" },
  { id: "patientName", label: "Patient Name" },
  { id: "birthDay", label: "DOB" },
  { id: "gender", label: "Gender" },
  { id: "serialNumber", label: "Device" },
  { id: "heartRate", label: "HR" },
  { id: "bloodGlucose", label: "Glucose" },
  { id: "respirationRate", label: "Respiration Rate" },
  { id: "bloodPressure", label: "BP" },
  { id: "spo2", label: "SPO2" },
  { id: "lastTimeActivity", label: "Last Sync" },
];

const PatientPopulationTable = ({ navigate }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const { patientList = [], patientPopulationsVitals = {}, loading } = useSelector((state) => state?.patient);

  const [patient, setPatients] = useState(patientList);
  const [filteredPatient, setFilteredPatient] = useState(patientList);
  const [searchQuery, setSearchQuery] = useState("");

  const { cardMetric, cardMeta, vitalName } = location.state || {};
  const getPatientIds = patientPopulationsVitals[cardMeta]?.[cardMetric];

  useEffect(() => {
    dispatch(getPatientList());
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(getPatientIds) && getPatientIds.length > 0) {
      const filteredPatients = patientList.filter((p) => getPatientIds.includes(parseInt(p.patientId)));
      setPatients(filteredPatients);
      setFilteredPatient(filteredPatients);
    } else {
      setPatients(patientList);
      setFilteredPatient(patientList);
    }
  }, [patientList, getPatientIds]);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredPatient(patient);
      return;
    }

    const lowerQuery = searchQuery.toLowerCase();
    const filteredList = patient.filter((p) =>
      p.patientFirstName?.toLowerCase().includes(lowerQuery) ||
      p.patientLastName?.toLowerCase().includes(lowerQuery) ||
      p.serialNumber?.toLowerCase().includes(lowerQuery)
    );

    setFilteredPatient(filteredList);
  }, [searchQuery, patient]);

  const handlePatientClick = (patient) => {
    navigate("/patient-chart", {
      state: { patientId: patient?.patientId },
    });
  };

  const rowsWithAction = (rows) => {
    return rows?.length && rows.map((e) => ({
      ...e,
      patientName: `${e.patientFirstName} ${e.patientLastName}`,
      birthDay: e.birthDay ? moment(e.birthDay).format('DD-MM-YYYY') : '',
      lastTimeActivity: e.lastTimeActivity ? moment(e.lastTimeActivity).format('DD-MM-YYYY, h:mm:ss a') : '',
      heartRate: JSON.parse(e?.lastDeviceData)?.HR,
      bloodGlucose: JSON.parse(e?.lastDeviceData)?.Glucose,
      spo2: JSON.parse(e?.lastDeviceData)?.SBP,
      bloodPressure: `${JSON.parse(e?.lastDeviceData)?.SBP}/${JSON.parse(e?.lastDeviceData)?.DBP}`,
      respirationRate: JSON.parse(e?.lastDeviceData)?.RR
    }));
  };

  return (
    <>
      {/* Header Section */}
      <Grid2
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ flexWrap: { xs: "wrap", md: "nowrap" }, p: { xs: "1", sm: "2" } }}
      >
        <Grid2
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "nowrap",
            justifyContent: { xs: "center", sm: "flex-start" },
          }}
        >
          <IconButton sx={{ color: "black" }} onClick={() => navigate('/patient-population')}>
            <img src={backArrow} alt="Back" width={24} height={24} />
          </IconButton>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ fontSize: { xs: "1.3rem", sm: "1.5rem", md: "2rem" } }}
          >
            <p className="text-capitalize">{cardMetric} vitals for {vitalName}</p>
          </Typography>
        </Grid2>
        <Grid2
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexDirection: { xs: "column", sm: "row", md: "row" },
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate('/view-all-patient')}
            sx={{
              bgcolor: "#fff",
              color: "#000000",
              border: "none",
              borderRadius: "50px",
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.3)",
              fontWeight: "500",
            }}
          >
            View All Patients
          </Button>
        </Grid2>
      </Grid2>

      {/* Search and Table Section */}
      <Box
        sx={{
          bgcolor: "#f5f5f5",
          mt: 3,
          border: "1px solid #ddd",
          borderRadius: "10px",
        }}
      >
        <Grid2
          container
          spacing={2}
          alignItems="center"
          sx={{ my: 0, borderRadius: "10px", p: 2, bgcolor: "white" }}
        >
          <Grid2
            item
            xs={12}
            md={6}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <TextField
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                width: { xs: "100%", md: "600px" },
                maxWidth: "750px",
                flexGrow: 1,
                borderRadius: "50px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "50px",
                },
              }}
              placeholder="Search by Name"
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton edge="start">
                      <Search sx={{ color: "#c1c5cd" }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid2>
        </Grid2>

        <CustomGenericTable
          title="CONNECTED"
          columns={columns}
          rows={rowsWithAction(filteredPatient) || []}
          rowCount={filteredPatient.length}
          handleNameClickCb={handlePatientClick}
        />
      </Box>
      <Loader loading={loading} />
    </>
  );
};

export default withNavigate(PatientPopulationTable);
